import { useLocation } from 'react-router-dom';

// Pages where the global filters have no effect (settings/profile, notification,
// and admin screens aren't scoped by brand/business unit/region/date range), so
// the filter bar and its toggle are hidden on these paths.
//
// /message-explorer is deliberately NOT listed: Message Explorer renders its
// own filter bar with the same chrome (toggle + scroll-collapse via UiContext),
// so the header toggle must stay available there — see
// useGlobalFiltersApplicable, which is what hides the *global* bar itself on
// that page.
const FILTERLESS_PATH_PREFIXES = ['/settings', '/notifications', '/admin', '/comparison'];

/** The consolidated email + SMS explorer (TECH-626 §3.3). */
export const MESSAGE_EXPLORER_PATH = '/message-explorer';

// Explorer-family paths: the explorer itself plus the per-message detail pages
// it links to (/emails/:id and /texts/:id). The legacy list paths (/emails,
// /email-explorer) redirect to the explorer, so they are covered as well.
const EXPLORER_PATH_PREFIXES = [MESSAGE_EXPLORER_PATH, '/email-explorer', '/emails', '/texts'];

function isExplorerPath(pathname: string): boolean {
  return EXPLORER_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** Whether the page-local filter bar applies (currently only Message Explorer and its detail pages). */
export function usePageFilterBarApplicable(): boolean {
  const { pathname } = useLocation();
  return isExplorerPath(pathname);
}

/**
 * Whether the GLOBAL filter bar (FilterContext-backed) applies to the current
 * page. Identical to useFilterBarApplicable except on the explorer, where the
 * page renders its own bar with page-local state instead.
 */
export function useGlobalFiltersApplicable(): boolean {
  const { pathname } = useLocation();
  if (isExplorerPath(pathname)) return false;
  return !FILTERLESS_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

// Detail pages (/emails/:id, /texts/:id) show a single message; the global
// filters don't apply to them, so the filter bar is hidden there (but not on
// the explorer itself).
const MESSAGE_DETAIL_PATH_PATTERN = /^\/(emails|texts)\/[^/]+$/;

/** Whether the global filter bar applies to the current page. */
export function useFilterBarApplicable(): boolean {
  const { pathname } = useLocation();
  if (MESSAGE_DETAIL_PATH_PATTERN.test(pathname)) return false;
  return !FILTERLESS_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

// Pages with no competitor-specific block (no radar/competitor table/isCompetitor
// badge) — the Competitors filter has no visible effect there, so it is hidden
// rather than shown as a no-op control. Dashboard reports not listed here
// (Brand Performance, Brand Deep Dive, Content Mix, Offer Strategy, Strategic
// Intent, Comparison, Monthly/Hourly Cadence) do use it.
const COMPETITOR_FILTERLESS_PATHS = [
  '/dashboard',
  '/dashboard/send-timing',
  '/dashboard/persona-performance',
  MESSAGE_EXPLORER_PATH,
  '/comparison',
];

/** Whether the Competitors filter applies to the current page. */
export function useCompetitorFilterApplicable(): boolean {
  const { pathname } = useLocation();
  return !COMPETITOR_FILTERLESS_PATHS.includes(pathname);
}
